import { Text } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Components
import DataTable from "../../../shared/DataTable/DataTable";
import LinkableTableCell from "../../../shared/DataTable/shared/LinkableTableCell";

// Utils
import { DataTableColumn, PaginatedApiResponse } from "../../../utils/types";

// Protos
import { Decimal } from "../../../utils/api/protos/messages/core.message_pb";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { getBillsForCase } from "../../../utils/api/payments";
import { PaymentsBillStatus } from "../../../utils/api/protos/messages/payments.message_pb";
import { EnvKeys, UserRole, dummyPaginatedApiResponse } from "../../../utils/consts";
import {
	convertUnixTimestampToLocalDate,
	createExternalLinkFromEnv,
	formatSnakeCaseName
} from "../../../utils/helpers";

interface ClaimPaymentsTableProps {
	caseId: number;
}

/** A simple table showing payments for a claim, fetched by the claim's PASS Case ID */
const ClaimPaymentsTable: FunctionComponent<ClaimPaymentsTableProps> = (
	props: ClaimPaymentsTableProps
): ReactElement => {
	const toast = useClaimsToast();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [paymentsData, setPaymentsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [searchParams] = useSearchParams();
	const { hasRole } = useAuth();
	const isAttorney = hasRole([UserRole.Provider]);

	/**
	 * Define columns to show in the DataTable
	 */
	const columns: Array<DataTableColumn> = [
		{
			header: "PASS Case ID",
			id: "case_id",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSelectable: false,
			isSortable: false,
			valueMap: (caseId: string) =>
				isAttorney ? (
					<Text>{caseId}</Text>
				) : (
					<LinkableTableCell
						To={createExternalLinkFromEnv(EnvKeys.PassUrl, caseId)}
						Contents={caseId}
						IsExternal={true}
					/>
				)
		},
		{
			header: "Total",
			id: "total",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false,
			valueMap: (total: string) => {
				// From an attorney's view, numbers should be positive, so multiple by -1
				const value = parseFloat(total) * (isAttorney ? -1 : 1);
				// Display positive numbers as green and negative as red with surrounding parenthesis
				return value > 0 ? (
					<Text color="green.500" fontWeight="500">
						{value.toLocaleString(undefined, { style: "currency", currency: "USD" })}
					</Text>
				) : (
					<Text color="red.500" fontWeight="500">
						({(value * -1).toLocaleString(undefined, { style: "currency", currency: "USD" })})
					</Text>
				);
			}
		},
		{
			header: "Number",
			id: "payment_number",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Status",
			id: "status",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Memo",
			id: "memo",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Claim ID",
			id: "claim_id",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Created On",
			id: "created_at",
			filterable: false,
			isDate: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		}
	];

	/**
	 * Helper function to allow for child objects to trigger a fetch of data
	 * with the active tab's cacheKey
	 */
	const handleFetchData = (): void => {
		setIsLoading(true);
		getBillsForCase(props.caseId)
			.then((response) => {
				// Build response data
				const returnedPaymentsData: Array<{ [key: string]: string | number }> = response.getBillList()?.map((bill) => ({
					id: bill.getId(),
					case_id: bill.getCaseId() ?? "",
					// Format the status to be based on the key, i.e. "PAYMENTS_STATUS_PAID_IN_FULL" becomes "Paid In Full"
					status: Object.entries(PaymentsBillStatus)
						.filter(([_, value]) => value === bill.getStatus())
						?.map(([key, value]) => formatSnakeCaseName(key.replaceAll("PAYMENTS_STATUS_", "")))?.[0],
					memo: bill.getMemo() ?? "",
					total: bill.getTotal()?.getValue() ? (bill.getTotal() as Decimal).getValue() : "",
					// The vendor payment's Document Number (NetSuite tranid), not the internal transaction id
					payment_number: bill.getPaymentNumber() ?? "",
					// Since the date should not have time, display in UTC so the day is correct
					claim_id: bill.getClaimId() ?? "Not Specified",
					created_at:
						convertUnixTimestampToLocalDate(bill.getCreatedAt()).toLocaleDateString("en-US", { timeZone: "UTC" }) ?? ""
				}));

				if (returnedPaymentsData) {
					setPaymentsData({
						data: returnedPaymentsData,
						meta: dummyPaginatedApiResponse.meta
					});
				} else {
					toast("Error", "There was a problem deciphering the pagination metadata", "error");
					console.error("Attempting to parse pagination metadata that does not exist!!");
				}
			})
			.catch((_) => {
				// Don't show errors in the UI, it is already logged in the console
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const getCustomSelectedActions = (): ReactElement => <></>;

	const getCustomHeader = (): ReactElement => {
		return <></>;
	};

	return (
		<>
			<DataTable
				data={paymentsData}
				columns={columns}
				refetchTrigger={handleFetchData}
				customHeaderActions={getCustomHeader()}
				customSelectedActions={getCustomSelectedActions}
				existingParams={searchParams}
				shouldUpdateUrl={true}
				shouldShowFilterToggle={false}
				shouldShowSelection={false}
				shouldShowTableContainer={false}
				shouldShowPagination={false}
				isLoading={isLoading}
			/>
			{paymentsData.data.length === 0 && !isLoading && (
				<Text pt="3" fontStyle="italic">
					There are no payments to show
				</Text>
			)}
		</>
	);
};

export default ClaimPaymentsTable;
